import { useState } from 'react';

import { userPhotoSrc } from '@/modules/users-admin/user-photo-rest';
import { cn } from '@/shared/lib/cn';

interface UserAvatarProps {
  name: string;
  /** Relative `photo_url` from the API; null/undefined falls back to initials. */
  photo_url?: string | null;
  size?: number;
  className?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Round avatar: the staff photo when there is one (and it loads), otherwise initials. */
export function UserAvatar({ name, photo_url, size = 40, className }: UserAvatarProps): JSX.Element {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : userPhotoSrc(photo_url);
  const style = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        loading="lazy"
        onError={() => setFailed(true)}
        style={style}
        className={cn('shrink-0 rounded-full bg-muted object-cover', className)}
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={name}
      style={style}
      className={cn(
        'bg-primary/10 text-primary inline-flex shrink-0 items-center justify-center rounded-full font-medium',
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}
